import {
  Box,
  Modal,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ModalBaseStyles, ModalHeader } from "../Common/styles/modal";
import { NoDataContainer, StyledTableCell } from "../Common/styles/table";
import { useEffect, useState } from "react";
import { errorToastMessage } from "../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { useNavigate } from "react-router-dom";
import { Edit, Visibility } from "@mui/icons-material";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  studyId: any;
  formId: string;
  type?: "platform" | "study";
  canEdit: boolean;
};

const LanguageTranslationModal = ({
  showModal,
  closeModal,
  studyId,
  formId,
  type = "study",
  canEdit,
}: Props) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const prefix =
          type === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
        let url = `${prefix}form-translations/${formId}/status`;
        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;

        const newData = data?.map((item: any) => ({
          label: item?.label,
          lang: item?.lang,
          status:
            item?.total > 0 && item?.total === item?.completed
              ? "Completed"
              : "Incomplete",
        }));

        setData(newData || []);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [studyId, formId, type]);

  const handleNavigate = (actiontype: string, lang: string) => {
    if (actiontype === "edit") {
      if (type === "platform") {
        navigate(`/app/designer/form/${formId}/edit-translation?lang=${lang}`);
      } else {
        navigate(
          `/studies/${studyId}/study-designer/form/${formId}/edit-translation?lang=${lang}`
        );
      }
    } else {
      if (type === "platform") {
        navigate(`/app/designer/form/${formId}/preview?lang=${lang}`);
      } else {
        navigate(
          `/studies/${studyId}/study-designer/form/${formId}/preview?lang=${lang}`
        );
      }
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={{ ...ModalBaseStyles, width: "60vw", minHeight: "20vh" }}>
        <ModalHeader title={"Language Translation"} onCloseClick={closeModal} />

        {!loading && data?.length > 0 && (
          <Box
            sx={{
              maxHeight: "calc(100vh - 295px)",
              overflow: "auto",
              mb: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Languages</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data?.map((row: any) => (
                  <TableRow key={row?.lang}>
                    <StyledTableCell>{row?.label}</StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "regular",
                          color:
                            row?.status === "Completed" ? "#70AE71" : "#FF7164",
                        }}
                      >
                        {row?.status}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {canEdit && (
                          <IconButton
                            onClick={() => handleNavigate("edit", row?.lang)}
                          >
                            <Edit sx={{ color: "text.secondary" }} />
                          </IconButton>
                        )}

                        <IconButton
                          onClick={() => handleNavigate("preview", row?.lang)}
                        >
                          <Visibility sx={{ color: "text.secondary" }} />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <CircularProgress size={25} />
          </Box>
        )}
        {!loading && data.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
      </Box>
    </Modal>
  );
};

export default LanguageTranslationModal;
